import { useState, useEffect, useCallback, ReactNode } from 'react'
import styled from 'styled-components'
import { usePopper } from 'react-popper'
import { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset'

import coreTheme from '../../styles/theme'

import InfoLightIcon from '../icons/InfoLight'
import IconClose from '../icons/AirbnbClose'
import InvisibleButton from './InvisibleButton'

const IconWrapper = styled(InvisibleButton)`
  appearance: none;
  outline: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;

  position: relative;
  top: 2px;
  margin-left: 8px;

  display: inline-block;
  color: #999;

  &:hover {
    color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};
  }

  svg {
    display: block;
    width: 16px;
    height: 16px;
  }
`
type WrapperProps = {
  backgroundColor: string
}
const Wrapper = styled.div<WrapperProps>`
  position: absolute;
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  background-color: ${({ backgroundColor }) => backgroundColor};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
  border-radius: 6px;
`

type ArrowProps = {
  foregroundColor: string
}
const Arrow = styled.div<ArrowProps>`
  position: absolute;
  height: 24px;
  width: 24px;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    left: 6px;
    top: -6px;

    background: ${({ foregroundColor }) => foregroundColor};
    transform: rotate(45deg);
    box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.1);
  }

  ${Wrapper}[data-popper-placement^='top'] > & {
    top: 100%;
  }

  ${Wrapper}[data-popper-placement^='bottom'] > & {
    bottom: 100%;

    &:after {
      top: auto;
      bottom: -6px;
      box-shadow: 2px 0px 4px 0 rgba(0, 0, 0, 0.15);
    }
  }
`

const CloseButton = styled.button`
  display: block;
  appearance: none;
  padding: 0;
  margin: 0;
  border: none;
  outline: none;
  background-color: transparent;
  position: absolute;
  top: 12px;
  right: 12px;

  svg {
    display: block;
    width: 14px;
    height: 14px;
    pointer-events: none;
  }
`

const Content = styled.div`
  padding: 16px 35px 16px 16px;
  font-weight: 300;
  text-transform: none;
`

type TooltipPopoverProps = {
  referenceElement: Element
  placement?:
    | 'top'
    | 'bottom'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
  zIndex?: number
  backgroundColor?: string
  children?: ReactNode
  onClose: () => unknown
}

const dynamicPopperOffset: OffsetsFunction = ({
  placement: actualPlacement,
}) => {
  if (actualPlacement === 'top' || actualPlacement === 'bottom') {
    return [0, 12]
  }
  if (actualPlacement.includes('start')) {
    return [-12, 12]
  }
  return [12, 12]
}

function useClickOutside(element: HTMLElement, onClickOutside: () => unknown) {
  const onDocumentClick = useCallback(
    (e: MouseEvent) => {
      if (!element?.contains(e.target as HTMLElement)) {
        onClickOutside()
      }
    },
    [element, onClickOutside],
  )

  useEffect(() => {
    document.addEventListener('click', onDocumentClick, { capture: true })

    return () => {
      document.removeEventListener('click', onDocumentClick, { capture: true })
    }
  }, [onDocumentClick])
}

function useGlobalEscapeKeyHandler(onKeyUp: () => unknown) {
  const onDocumentKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (['Esc', 'Escape'].includes(e.key)) {
        e.preventDefault()
        onKeyUp()
      }
    },
    [onKeyUp],
  )

  useEffect(() => {
    document.addEventListener('keyup', onDocumentKeyUp, { capture: true })

    return () => {
      document.removeEventListener('keyup', onDocumentKeyUp, { capture: true })
    }
  }, [onDocumentKeyUp])
}

const TOOLTIP_POPOVER_ID = 'tooltip'
export function TooltipPopover({
  referenceElement,
  placement = 'top-end',
  zIndex = 1,
  backgroundColor = coreTheme['-ds-neutral-200']?.value,
  children,
  onClose,
}: TooltipPopoverProps) {
  const [popoverElement, setPopoverElement] = useState<HTMLElement>(null)
  const [arrowElement, setArrowElement] = useState<HTMLElement>(null)

  useClickOutside(popoverElement, onClose)
  useGlobalEscapeKeyHandler(onClose)

  useEffect(() => {
    referenceElement?.setAttribute('aria-describedby', TOOLTIP_POPOVER_ID)

    return () => referenceElement?.removeAttribute('aria-describedby')
  }, [referenceElement])

  const { styles, attributes } = usePopper(referenceElement, popoverElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement, padding: 6 } },
      { name: 'offset', options: { offset: dynamicPopperOffset } },
      {
        name: 'flip',
        options: {
          padding: 16,
          allowedAutoPlacements: ['top', 'bottom'],
        },
      },
      {
        name: 'preventOverflow',
        options: { padding: 16, boundary: document.documentElement },
      },
    ],
  })

  return (
    <Wrapper
      id={TOOLTIP_POPOVER_ID}
      ref={setPopoverElement}
      backgroundColor={backgroundColor}
      style={{ ...styles.popper, zIndex }}
      {...attributes.popper}
    >
      <CloseButton
        className="js-prevent-global-events"
        aria-label="Close tooltip"
        onClick={(e) => {
          e.preventDefault()
          onClose()
        }}
      >
        <IconClose />
      </CloseButton>
      <Content>{children}</Content>
      <Arrow
        ref={setArrowElement}
        style={styles.arrow}
        foregroundColor={backgroundColor}
      />
    </Wrapper>
  )
}

type TooltipProps = Omit<
  TooltipPopoverProps,
  'referenceElement' | 'onClose'
> & {
  initiallyOpen?: boolean
  icon?: ReactNode
}

const Tooltip = ({
  initiallyOpen = false,
  placement = 'top-end',
  zIndex = 1,
  backgroundColor = coreTheme['-ds-neutral-200']?.value,
  children,
  icon,
}: TooltipProps) => {
  const [iconElement, setIconElement] = useState<HTMLElement>(null)
  const [open, setOpen] = useState(initiallyOpen)

  return (
    <>
      <IconWrapper
        ref={setIconElement}
        aria-label="Open tooltip"
        onClick={() => {
          setOpen(!open)
        }}
      >
        {icon || <InfoLightIcon />}
      </IconWrapper>
      {open && (
        <TooltipPopover
          referenceElement={iconElement}
          placement={placement}
          zIndex={zIndex}
          backgroundColor={backgroundColor}
          onClose={() => setOpen(false)}
        >
          {children}
        </TooltipPopover>
      )}
    </>
  )
}

export default Tooltip
